/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import {
  IonContent,
  IonButton,
  IonIcon,
  IonModal,
  isPlatform,
} from "@ionic/react";
import { chevronBack, add, close, barcode } from "ionicons/icons";
// Import the barcode scanning plugin
import {
  BarcodeScanner,
  BarcodeFormat,
} from "@capacitor-mlkit/barcode-scanning";
import useAuth from "../../../hooks/useAuth";
import {
  ConnectedDevice,
  parseQRCodeData,
  attachDevice,
} from "../../../services/device";
import { BiwazeEnum } from "../../../utils/constants";
import { RequestPopup } from "../../layout/Guest/Components/RequestPopup";
import { STEP_IDS } from "../Onboarding";
import analytics from "../../../services/firebaseAnalytics";

interface AddAnotherDeviceProps {
  usesBiWazeDevice: any | null;
  nextStep: (index?: number) => void;
  prevStep: () => void;
  connectedDevices: ConnectedDevice[] | undefined;
  setConnectedDevices: (devices: ConnectedDevice[]) => void;
  setUsesBiWazeDevice: (device: any) => void;
  buttonText?: string;
  navigateToStep?: (step: string) => void;
}

const AddAnotherDevice: React.FC<AddAnotherDeviceProps> = ({
  nextStep,
  prevStep,
  setConnectedDevices,
  connectedDevices,
  usesBiWazeDevice,
  setUsesBiWazeDevice,
  buttonText = "Next",
  navigateToStep,
}) => {
  const [devices, setDevices] = useState<ConnectedDevice[]>(
    connectedDevices || []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDeviceModalOpen, setIsAddDeviceModalOpen] = useState(false);
  const [scannedData, setScannedData] = useState<any | null>({
    deviceType: "",
    serialNumber: "",
    hotp: "",
  });
  const [showScanAgain, setShowScanAgain] = useState(false);
  const [deviceLinkedInSession, setDeviceLinkedInSession] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const popupShownRef = React.useRef(false);
  const [popupContent, setPopupContent] = useState<{
    header?: string;
    message: string;
    buttons?: string[];
    buttonAction?: () => void;
  }>({ message: "" });

  const { user, isNativePlatform } = useAuth();

  const showPopup = (content: {
    header?: string;
    message: string;
    buttons?: string[];
    buttonAction?: () => void;
  }) => {
    setPopupContent(content);
    setPopupOpen(true);
    popupShownRef.current = true;
  };

  useEffect(() => {
    if (popupShownRef.current && !popupOpen) {
      setShowContent(true);
    }
  }, [popupOpen]);

  useEffect(() => {
    if (!Array.isArray(connectedDevices)) return;

    setDevices(connectedDevices);

    handleScanDevice();
    const logAnalyticsEvent = async () => {
      await analytics.logEvent("onboarding_add_another_device_viewed", {});
    };
  }, []);

  // Function to check if app has camera permission
  const checkPermissions = async () => {
    if (isPlatform("android")) {
      const isAvailable =
        await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
      console.log(
        "Google Barcode Scanner Module available:",
        JSON.stringify(isAvailable)
      );
      if (!isAvailable.available) {
        await BarcodeScanner.installGoogleBarcodeScannerModule();
        console.log("Initiated installation of Google Barcode Scanner Module");
      }
    }
    const { camera } = await BarcodeScanner.checkPermissions();
    return camera;
  };

  // Function to request camera permission
  const requestPermissions = async () => {
    const { camera } = await BarcodeScanner.requestPermissions();
    return camera;
  };

  const checkConfirmation = (scannedString: string) => {
    console.log("Scanned string:", scannedString);
    const { deviceType, serialNumber, hotp } = parseQRCodeData(scannedString);
    setScannedData({ deviceType, serialNumber, hotp });
    // if (
    //   (deviceType?.toLowerCase().includes("cough") &&
    //     connectedDevices?.some((d) =>
    //       d.name?.toLowerCase().includes("cough")
    //     )) ||
    //   (deviceType?.toLowerCase().includes("clear") &&
    //     connectedDevices?.some((d) => d.name?.toLowerCase().includes("clear")))
    // ) {
    //   setIsAddDeviceModalOpen(true);
    // } else {
    addScannedDevice(deviceType, serialNumber, hotp);
    // }
  };

  // Function to add scanned device to the list
  interface AddScannedDeviceParams {
    deviceType: string;
    serialNumber: string;
    hotp: string;
  }

  const addScannedDevice = async (
    deviceType: AddScannedDeviceParams["deviceType"],
    serialNumber: AddScannedDeviceParams["serialNumber"],
    hotp: AddScannedDeviceParams["hotp"]
  ): Promise<void> => {
    try {
      console.log("Device Type:", deviceType);
      console.log("Serial Number:", serialNumber);
      console.log("HOTP:", hotp);

      if (user?.profile_id) {
        const updatedDevices: ConnectedDevice[] = await attachDevice(
          user.profile_id,
          deviceType,
          serialNumber,
          hotp
        );
        setConnectedDevices(updatedDevices);
        setDevices(updatedDevices);
        setDeviceLinkedInSession(true);

        showPopup({
          header: "Success",
          message: `Successfully linked Biwaze® ${
            typeof deviceType === "string"
              ? deviceType.charAt(0).toUpperCase() + deviceType.slice(1)
              : ""
          } with Serial: ${serialNumber}`,
          buttons: ["OK"],
          buttonAction: () => {
            nextStep();
          },
        });
      }
    } catch (error: any) {
      console.error("Error in addScannedDevice:", error);
      const errorMessage: string = error?.message || "Unknown error";

      if (errorMessage === "Invalid HOTP") {
        setShowScanAgain(true);
        showPopup({
          header: "Error",
          message:
            "This code seems to have been scanned before. Reopen the QR code on your BiWaze® device and retry.",
          buttons: ["OK"],
          buttonAction: () => {
            setPopupOpen(false);
          },
        });
      } else if (errorMessage.includes("Device already attached")) {
        setShowScanAgain(true);
        showPopup({
          header: "Error",
          message: "This device is already linked to your account.",
          buttons: ["OK"],
          buttonAction: () => {
            setPopupOpen(false);
          },
        });
      } else if (
        errorMessage.includes("AbortError") ||
        errorMessage.includes("scan canceled")
      ) {
        setShowScanAgain(true);
        console.log("Scan was canceled by the user.");
        showPopup({
          header: "Scan Canceled",
          message:
            "If you wish to scan again, close this message and tap Scan QR Code.",
          buttons: ["Go Back"],
          buttonAction: () => {
            if (navigateToStep) {
              navigateToStep(STEP_IDS.DEVICE_USAGE_CONFIRM);
            } else {
              prevStep();
            }
          },
        });
        // Remove the duplicate prevStep() call here
      } else if (errorMessage === "Invalid QR code format") {
        setShowScanAgain(true);
        showPopup({
          header: "Error",
          message:
            "Invalid QR code format. Please try again with a valid Arc Connect QR from BiWaze® system",
          buttons: ["OK"],
          buttonAction: () => {
            if (navigateToStep) {
              navigateToStep(STEP_IDS.DEVICE_USAGE_CONFIRM);
            } else {
              nextStep(2);
            }
          },
        });
      } else {
        setShowScanAgain(true);
        showPopup({
          header: "Error",
          message: `Error adding device: ${errorMessage.substring(0, 100)}`,
          buttons: ["OK"],
          buttonAction: () => {
            if (navigateToStep) {
              navigateToStep(STEP_IDS.DEVICE_USAGE_CONFIRM);
            } else {
              nextStep(2);
            }
          },
        });
      }
    }
  };

  // Function to scan QR code
  const scanBarcode = async () => {
    try {
      await BarcodeScanner.setZoomRatio({ zoomRatio: 0.5 });
      const { barcodes } = await BarcodeScanner.scan({
        formats: [BarcodeFormat.QrCode],
      });

      if (barcodes.length > 0 && barcodes[0].rawValue) {
        await checkConfirmation(barcodes[0].rawValue);
      } else {
        console.log("No barcodes detected");
        showPopup({
          header: "No QR Code Found",
          message: "Please try scanning again.",
          buttons: ["OK"],
          buttonAction: () => {
            setPopupOpen(false);
          },
        });
      }
    } catch (error: any) {
      const errorMessage = error?.message || "Unknown error";
      console.error(`Scanning failed: ${errorMessage}`, error);

      if (errorMessage === "Invalid HOTP") {
        setShowScanAgain(true);
        showPopup({
          header: "Error",
          message:
            "This code seems to have been scanned before. Reopen the QR code on your BiWaze device and retry.",
          buttons: ["OK"],
          buttonAction: () => {
            setPopupOpen(false);
          },
        });
      } else if (errorMessage.includes("Device already attached")) {
        setShowScanAgain(true);
        showPopup({
          header: "Error",
          message: "This device is already linked to your account.",
          buttons: ["OK"],
          buttonAction: () => {
            setPopupOpen(false);
          },
        });
      } else if (errorMessage === "Invalid QR code format") {
        setShowScanAgain(true);
        showPopup({
          header: "Error",
          message:
            "Invalid QR code format. Please try again with a valid BiWaze QR code.",
          buttons: ["OK"],
          buttonAction: () => {
            setPopupOpen(false);
          },
        });
      } else if (
        errorMessage.includes("Permission denied") ||
        errorMessage.includes("access")
      ) {
        setShowScanAgain(true);
        showPopup({
          header: "Camera Access Error",
          message: "Please allow camera access to scan QR codes.",
          buttons: ["OK"],
          buttonAction: () => {
            setPopupOpen(false);
          },
        });
      } else if (
        errorMessage.includes("scan canceled") ||
        errorMessage.includes("AbortError")
      ) {
        setShowScanAgain(true);
        console.log("Scan was canceled by the user.");
        showPopup({
          header: "Scan Canceled",
          message:
            "If you wish to scan again, close this message and tap Scan QR Code.",
          buttons: ["OK"],
          buttonAction: () => {
            setPopupOpen(false);
          },
        });
      } else {
        setShowScanAgain(true);
        showPopup({
          header: "Error",
          message: `Scanning failed: ${errorMessage.substring(0, 100)}`,
          buttons: ["OK"],
        });
      }
    }
  };

  // Updated function to handle device scanning
  const handleScanDevice = async () => {
    if (!isNativePlatform) {
      setShowScanAgain(true);
      showPopup({
        header: "Unsupported Platform",
        message: "QR code scanning is only available on mobile devices.",
        buttons: ["OK"],
        buttonAction: () => nextStep(),
      });

      return;
    }

    let permission = await checkPermissions();

    if (permission !== "granted") {
      permission = await requestPermissions();

      if (permission !== "granted") {
        showPopup({
          header: "Permission Required",
          message:
            "Camera access is needed to scan your device's QR code and securely link it to your account. Please grant permission in your device settings.",
          buttons: ["OK"],
        });
        return;
      }
    }

    scanBarcode();
  };

  // Handle adding another device
  const handleAddAnotherDevice = () => {
    if (devices.length >= 1 || devices.length === 0) {
      nextStep(2);
      handleScanDevice();
    } else {
      handleScanDevice();
    }
  };

  return (
    <IonContent className="ion-padding onboarding-section">
      <div className="navigation-header">
        <button className="back-text-button" onClick={prevStep}>
          <IonIcon icon={chevronBack} className="back-icon" />
          <span>Back</span>
        </button>
      </div>

      {showContent && (
        <div className="success-container">
          <div className="success-message">
            <h2 className="success-heading">
              {deviceLinkedInSession
                ? "Great! You have successfully linked"
                : devices.length > 0
                ? "Currently linked"
                : "Link your BiWaze® device"}
            </h2>
            {devices.length > 0 ? (
              devices.map((device, index) => (
                <React.Fragment key={index}>
                  <div className="device-info">
                    <h1 className="device-name">{device.name}</h1>
                    <p className="device-serial mb-4">
                      SN: {device.serialNumber}
                    </p>
                  </div>
                  {index < devices.length - 1 && (
                    <div
                      className="device-divider"
                      style={{ borderTop: "1px solid #e0e0e0", margin: "16px 0" }}
                    />
                  )}
                </React.Fragment>
              ))
            ) : (
              <div className="device-info">
                <p className="no-devices">No devices linked yet.</p>
                <p className="scan-instructions">
                  Scan the QR code on your BiWaze <sup> ®</sup> device to get
                  started.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {showContent && (
        <div
          className="button-stack-container flex flex-col gap-10 items-center w-full"
          style={{
            left: 0,
            right: 0,
            padding: "0 16px",
          }}
        >
          <div className="button-stack-container w-full flex flex-col  gap-3">
            {devices.length <= 2 && (
              <button
                className="add-device-button w-[250px] flex"
                onClick={handleAddAnotherDevice}
              >
                <IonIcon icon={add} className="add-icon" />
                {devices.length > 0 ? "Add another device" : "Add device"}
              </button>
            )}
            <IonButton
              expand="block"
              className="primary-button"
              onClick={() => nextStep()}
            >
              {buttonText}
            </IonButton>
            <button className="skip-link">{/* Skip for now */}</button>
          </div>
        </div>
      )}

      {/* Add Device Modal */}
      <IonModal
        isOpen={isAddDeviceModalOpen}
        onDidDismiss={() => setIsAddDeviceModalOpen(false)}
        className="connect-modal"
      >
        <div className="p-6 flex flex-col items-center">
          <div className="w-full flex justify-end mb-4">
            <button
              onClick={() => setIsAddDeviceModalOpen(false)}
              className="text-gray-400"
            >
              <IonIcon icon={close} style={{ fontSize: "24px" }} />
            </button>
          </div>

          <h2 className="text-2xl font-bold mb-6 text-center">
            Add a BiWaze<sup>®</sup>Device
          </h2>

          <p className="text-center mb-8">
            {`You already have a ${
              scannedData.deviceType?.includes("cough")
                ? BiwazeEnum.COUGH
                : scannedData.deviceType?.includes("clear")
                ? BiwazeEnum.CLEAR
                : ""
            } device connected. This will override the existing device. Do you want to proceed?`}
          </p>

          <IonButton
            expand="block"
            onClick={async () => {
              addScannedDevice(
                scannedData.deviceType,
                scannedData.serialNumber,
                scannedData.hotp
              );
              setIsAddDeviceModalOpen(false);
            }}
            className="scan-btn"
            style={{
              "--border-radius": "30px",
              "--background": "#05ACD9",
              color: "#FFFFFF",
              height: "52px",
              width: "100%",
            }}
          >
            Proceed
          </IonButton>
        </div>
      </IonModal>

      {popupOpen && (
        <RequestPopup
          title={popupContent.header}
          description={popupContent.message}
          onClose={() => setPopupOpen(false)}
          buttonText={popupContent.buttons ? popupContent.buttons[0] : "OK"}
          onProceed={popupContent.buttonAction}
          buttonClassName="primary-button"
        />
      )}
    </IonContent>
  );
};

export default AddAnotherDevice;
